/**
 * @budgeting101/api-client — Typed fetch wrapper
 *
 * Usage:
 *   const client = createApiClient({ baseUrl: '/api' });
 *   const txs = await client.get('/transactions', TransactionSchema.array());
 *
 * Token management:
 *   - Reads access token from in-memory store (set via setToken).
 *   - On 401 → dispatches 'auth:expired' custom event; caller (useAuth) handles redirect.
 *
 * Error truth:
 *   - The server envelope `{ error: { code, message, details, referenceId } }`
 *     is unwrapped into a typed ApiError; raw transport text never surfaces.
 *   - Offline mutations are refused before anything is sent (code OFFLINE):
 *     the caller can promise "nothing changed" truthfully.
 *   - A connection that drops mid-send is NOT the same thing: the outcome is
 *     unknown (code NETWORK_UNCONFIRMED) and must not be blindly retried
 *     without an idempotency key.
 *
 * No floats enter or leave this client — amounts are always strings.
 */

import { z } from 'zod';
import type { ApiError } from './types.js';

export interface ClientOptions {
  baseUrl: string;
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

const MUTATING_METHODS: ReadonlySet<HttpMethod> = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

let _accessToken: string | null = null;
let _accessTokenVersion = 0;

export function setToken(token: string | null): void {
  _accessToken = token;
  _accessTokenVersion += 1;
}

export function getToken(): string | null {
  return _accessToken;
}

/**
 * Monotonic identity for the currently installed access token. Requests use
 * this to prove that a late 401 belongs to the session still on screen.
 */
export function getTokenVersion(): number {
  return _accessTokenVersion;
}

export class ApiClientError extends Error {
  constructor(
    public readonly apiError: ApiError,
    public readonly status: number,
  ) {
    super(apiError.message);
    this.name = 'ApiClientError';
  }
}

/** Coarse, UI-facing classification of an API failure. */
export type ApiErrorKind =
  | 'offline'              // nothing was sent; safe to retry after reconnect
  | 'network'              // send attempted, outcome unknown
  | 'auth'                 // session invalid or expired
  | 'permission'           // authenticated but not allowed
  | 'not-found'
  | 'validation'           // request rejected before any change
  | 'stale-preview'        // server facts changed since the preview
  | 'already-decided'      // another actor decided this first
  | 'idempotency-payload'  // same Idempotency-Key with a different payload
  | 'rate-limit'           // server refused due to request rate; not an unknown failure
  | 'conflict'             // other domain conflict (e.g. budget block)
  | 'client'               // another explicit 4xx answer; never retry as unknown
  | 'server'
  | 'unknown';

export function classifyApiError(error: unknown): ApiErrorKind {
  const status = error instanceof ApiClientError
    ? error.status
    : error && typeof error === 'object' && typeof (error as { status?: unknown }).status === 'number'
      ? (error as { status: number }).status
      : undefined;
  const code = error instanceof ApiClientError ? error.apiError.code : undefined;

  if (status === undefined) return 'unknown';

  switch (code) {
    case 'OFFLINE':                       return 'offline';
    case 'NETWORK':
    case 'NETWORK_UNCONFIRMED':           return 'network';
    case 'PREVIEW_CHANGED':               return 'stale-preview';
    case 'ALREADY_DECIDED':               return 'already-decided';
    case 'APPROVAL_ALREADY_DECIDED':      return 'already-decided';
    case 'IDEMPOTENCY_PAYLOAD_MISMATCH':  return 'idempotency-payload';
    case 'RATE_LIMITED':
    case 'TOO_MANY_REQUESTS':             return 'rate-limit';
    case 'PERMISSION_DENIED':
    case 'FORBIDDEN':                     return 'permission';
    case 'VALIDATION':
    case 'INVALID_REQUEST':               return 'validation';
    case 'UNAUTHORIZED':                  return 'auth';
    case 'NOT_FOUND':                     return 'not-found';
  }

  if (status === 401) return 'auth';
  if (status === 403) return 'permission';
  if (status === 404) return 'not-found';
  if (status === 429) return 'rate-limit';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (status >= 400 && status < 500) return 'client';
  if (status >= 500) return 'server';
  return 'unknown';
}

/** Unwrap the `{ error: { … } }` envelope (or a legacy flat body) into ApiError. */
function toApiError(payload: unknown, status: number): ApiError {
  const envelope = payload && typeof payload === 'object' && 'error' in payload
    ? (payload as { error: unknown }).error
    : payload;

  if (envelope && typeof envelope === 'object') {
    const raw = envelope as { code?: unknown; message?: unknown; details?: unknown; referenceId?: unknown };
    return {
      status,
      code: typeof raw.code === 'string' ? raw.code : 'UNKNOWN',
      message: typeof raw.message === 'string' ? raw.message : 'The request failed.',
      details: raw.details,
      referenceId: typeof raw.referenceId === 'string' ? raw.referenceId : undefined,
    };
  }

  return { status, code: 'UNKNOWN', message: 'The request failed.' };
}

export interface RequestOptions {
  /**
   * Sent as the Idempotency-Key header so the server can deduplicate
   * retries of the same command. Generate via newIdempotencyKey() and keep
   * the same key when retrying the same logical command.
   */
  idempotencyKey?: string;
  /** Strong aggregate validator required by reviewed Shared commands. */
  ifMatch?: string;
}

export function createApiClient({ baseUrl }: ClientOptions) {
  async function request<T>(
    method: HttpMethod,
    path: string,
    schema: z.ZodType<T>,
    body?: unknown,
    params?: Record<string, string | number | boolean | undefined>,
    options?: RequestOptions,
  ): Promise<T> {
    const isMutation = MUTATING_METHODS.has(method);

    // Block commits while offline: nothing is sent, so the UI can keep the
    // draft and truthfully say no money state changed.
    if (isMutation && typeof navigator !== 'undefined' && navigator.onLine === false) {
      throw new ApiClientError({
        status: 0,
        code: 'OFFLINE',
        message: 'You are offline. Nothing was sent — your draft is kept on this device.',
      }, 0);
    }

    const url = new URL(`${baseUrl}${path}`, window.location.origin);

    if (params) {
      for (const [k, v] of Object.entries(params)) {
        if (v !== undefined) url.searchParams.set(k, String(v));
      }
    }

    const requestToken = _accessToken;
    const requestTokenVersion = _accessTokenVersion;
    const headers: Record<string, string> = {
      'Accept': 'application/json',
    };

    // Fastify rejects an empty request advertised as JSON before it reaches a
    // bodyless command handler. Only declare JSON when a body is actually
    // present (for example, archive-toggle and invoice preview are bodyless).
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (requestToken) {
      headers['Authorization'] = `Bearer ${requestToken}`;
    }

    if (options?.idempotencyKey) {
      headers['Idempotency-Key'] = options.idempotencyKey;
    }
    if (options?.ifMatch) {
      headers['If-Match'] = options.ifMatch;
    }

    let res: Response;
    try {
      res = await fetch(url.toString(), {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch {
      throw new ApiClientError({
        status: 0,
        code: isMutation ? 'NETWORK_UNCONFIRMED' : 'NETWORK',
        message: isMutation
          ? 'The connection dropped while sending. The result is unknown — refresh the relevant list and check for the result before retrying.'
          : 'Could not reach the server.',
      }, 0);
    }

    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:expired', {
        detail: { tokenVersion: requestTokenVersion },
      }));
      const err: ApiError = { status: 401, code: 'UNAUTHORIZED', message: 'Session expired.' };
      throw new ApiClientError(err, 401);
    }

    if (!res.ok) {
      let payload: unknown;
      try {
        payload = await res.json();
      } catch {
        payload = undefined;
      }
      throw new ApiClientError(toApiError(payload, res.status), res.status);
    }

    if (res.status === 204) {
      return undefined as unknown as T;
    }

    const json = await res.json();
    return schema.parse(json);
  }

  return {
    get<T>(
      path: string,
      schema: z.ZodType<T>,
      params?: Record<string, string | number | boolean | undefined>,
      options?: RequestOptions,
    ) {
      return request('GET', path, schema, undefined, params, options);
    },
    post<T>(path: string, schema: z.ZodType<T>, body: unknown, options?: RequestOptions) {
      return request('POST', path, schema, body, undefined, options);
    },
    put<T>(path: string, schema: z.ZodType<T>, body: unknown, options?: RequestOptions) {
      return request('PUT', path, schema, body, undefined, options);
    },
    patch<T>(path: string, schema: z.ZodType<T>, body: unknown, options?: RequestOptions) {
      return request('PATCH', path, schema, body, undefined, options);
    },
    delete<T>(path: string, schema: z.ZodType<T>, options?: RequestOptions) {
      return request('DELETE', path, schema, undefined, undefined, options);
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
